import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, Info, X, Loader } from 'lucide-react';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'loading';
  progress?: number;
}

interface ToastContextType {
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'loading', duration?: number) => string;
  updateToast: (id: string, message: string, type?: 'success' | 'error' | 'info' | 'loading', progress?: number) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    // Return a no-op implementation if ToastProvider is not available
    // This prevents crashes in components that might render outside the provider
    return {
      showToast: () => '',
      updateToast: () => {},
      hideToast: () => {},
    };
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'loading', duration = 5000): string => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, message, type }]);

    if (type !== 'loading' && duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, duration);
    }

    return id;
  }, []);

  const updateToast = useCallback((id: string, message: string, type?: 'success' | 'error' | 'info' | 'loading', progress?: number) => {
    setToasts(prev =>
      prev.map(toast =>
        toast.id === id
          ? { ...toast, message, ...(type && { type }), ...(progress !== undefined && { progress }) }
          : toast
      )
    );
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />;
      case 'loading':
        return <Loader className="w-5 h-5 text-accent-600 dark:text-accent-400 animate-spin" />;
      default:
        return <Info className="w-5 h-5 text-accent-600 dark:text-accent-400" />;
    }
  };

  const getColorClasses = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/90 border-green-200 dark:border-green-700';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/90 border-red-200 dark:border-red-700';
      case 'loading':
        return 'bg-accent-50 dark:bg-accent-900/90 border-blue-200 dark:border-accent-700';
      default:
        return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, updateToast, hideToast }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-md">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg transition-all ${getColorClasses(toast.type)}`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getIcon(toast.type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${
                toast.type === 'error' 
                  ? 'text-red-900 dark:text-red-100' 
                  : toast.type === 'success'
                  ? 'text-green-900 dark:text-green-100'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                {toast.message}
              </p>
              {toast.progress !== undefined && (
                <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div
                    className="bg-accent-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${toast.progress}%` }}
                  />
                </div>
              )}
            </div>
            {toast.type !== 'loading' && (
              <button
                onClick={() => hideToast(toast.id)}
                className={`flex-shrink-0 transition-colors ${
                  toast.type === 'error'
                    ? 'text-red-600 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200'
                    : toast.type === 'success'
                    ? 'text-green-600 hover:text-green-700 dark:text-green-300 dark:hover:text-green-200'
                    : 'text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

